import { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Button, Modal, ModalBody, ModalFooter } from 'reactstrap';
import {
  useToast,
  useTranslator,
  cloudflareImage,
  mealPlaceholderImage,
  useAppContext,
} from '@front-activerse/common-components';

import { useOutletContext } from 'react-router-dom';
import DayPlanNutritions from '../../MealPlan/MealView/components/DayPlanNutritions';
import DayPlanMealRating from '../../MealPlan/MealView/components/DayPlanMealRating';
import InstacartButton from '../../MealPlan/MealView/components/InstacartButton';
import FriscoButton from '../../MealPlan/MealView/components/FriscoButton';

import { Link, useNavigate } from 'react-router-dom';

const FavMealDetails = ({
  meal,
  children,
  toggleInsertDate,
  removeMealFromFavorites,
  addMealToFavorites,
  rateMeal,
  refetch,
}) => {
  const videoRef = useRef();
  const ratingRef = useRef();

  const { t } = useTranslator();
  const [showModal, setShowModal] = useState(false);
  const { toastError, toastSuccess } = useToast();
  const { trackFirebaseEvent } = useOutletContext();
  const navigate = useNavigate();

  const [visiblePoster, setVisiblePoster] = useState(true);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const {
    consts: { APP_BRAND, FRISCO },
    images: { favouriteFull, playBtn, addToMealplan, addToTrackerIcon },
  } = useAppContext();

  useEffect(() => {
    trackFirebaseEvent('screen_view ', {
      firebase_screen: 'Favourite_details',
      meal_id: meal.id,
      favourites_category: meal.mealType.name,
    });
  }, []);

  const calculatePreparationTime = (minutes) => {
    const steps = [5, 15, 30, 45, 60];

    if (minutes === 0 || minutes == null) {
      return null;
    }

    for (const step of steps) {
      if (minutes <= step) {
        return `< ${step} min`;
      }
    }
    return `> ${steps[steps.length - 1]} min`;
  };

  const handleRemoveMeal = async () => {
    try {
      const response = await removeMealFromFavorites.removeMeal(meal.id);
      if (response.data.me.removeMealFromFavoritesByMealId.__typename === 'BasicMutationSuccess') {
        toastSuccess(
          <p>
            {t('mealPlan.favorites.notification.removedFromFav.content')}
            <Button color="link" onClick={() => handleAddMeal(meal.id)}>
              {t('meal.favorites.notification.removedFromFav.button')}
            </Button>
          </p>
        );
        navigate(-1);
      }
    } catch (error) {
      toastError();

      throw new Error(`Failed to remove meal from favorites: ${error}`);
    }
  };

  const handleAddMeal = async (mealId) => {
    try {
      await addMealToFavorites.addMeal(mealId);
    } catch (error) {
      toastError();

      throw new Error(`Failed to add meal to favorites: ${error}`);
    }
  };

  const renderMealVideo = () => {
    if (!meal.preparationVideoUrl) {
      return renderMealImage();
    }

    return (
      <div className="embed-responsive ratio ratio-1x1">
        <video
          ref={videoRef}
          src={meal.preparationVideoUrl}
          controls
          playsInline
          controlsList="nodownload"
          disablePictureInPicture
          onEnded={() => setVisiblePoster(true)}
        />
        {visiblePoster ? (
          <div
            style={{
              backgroundImage: `url(${meal.preparationImageUrl
                .replace('(', '%28')
                .replace(')', '%29')})`,
            }}
            className="embed-responsive-item d-flex align-items-center justify-content-center"
            onClick={() => playVideo(videoRef)}
          >
            <img src={playBtn} alt="" />
          </div>
        ) : (
          ''
        )}
      </div>
    );
  };

  const renderMealImage = () => {
    let mealImg;
    if (meal.preparationImageUrl) {
      mealImg = meal.preparationImageUrl;
    } else {
      mealImg = mealPlaceholderImage(meal.mealType, APP_BRAND);
    }

    const coverImage = cloudflareImage(mealImg, `${APP_BRAND}`, 'mealPlanMealDetails');

    return <img src={coverImage} className="img-fluid" width={768} height={768} alt="" />;
  };

  const playVideo = async (myVideoRef) => {
    setVisiblePoster(false);

    trackFirebaseEvent('user_action', {
      action: 'tap-watch_video',
      location: 'Meal_details',
      meal_id: meal.id,
      meal_category: meal.mealType.name,
    });

    try {
      await myVideoRef.current.play();
    } catch (err) {
      toastError();
      console.warn(err);
    }
  };

  const renderTags = () => {
    if (meal.mealCustomTag && meal.mealCustomTag.label) {
      return (
        <div className="tags">
          {meal.mealCustomTag && (
            <span
              className="tag me-1"
              style={{ backgroundColor: meal.mealCustomTag.color, color: '#fff' }}
            >
              {meal.mealCustomTag.label}
            </span>
          )}
          <span className="tag me-1">{meal.name}</span>
        </div>
      );
    }
    return (
      <div className="tags">
        <span className="tag me-1">{meal.name}</span>
      </div>
    );
  };

  const handleAddMealToTracker = () => {
    const isTrackerEnabled = localStorage.getItem('trackerEnabled') === 'true';

    if (isTrackerEnabled) {
      navigate(`/meals/day-plan/module/tracker/add-fav-meal/${meal.id}`);
    } else {
      toggleModal();
    }
  };

  const calculatedPreparationTime = calculatePreparationTime(meal.preparationTime.minutes);

  return (
    <>
      <Container className="py-md-4">
        <Row>
          <Col xl={{ size: 10, offset: 1 }}>
            <div className="page-container">
              <div className="meal-details">
                <Row>
                  <Col md="6">
                    <div className="meal-image-video">
                      {renderMealVideo()}

                      <Button className="add-remove-fav" color="link" onClick={handleRemoveMeal}>
                        <img src={favouriteFull} alt="" width="32" height="32" />
                      </Button>

                      {visiblePoster && (
                        <div className="meal-info2 d-md-none">
                          <Container>
                            {renderTags()}
                            <h1 className="meal-title title-large semi-bold white mb-0">
                              {meal.publicName}
                            </h1>
                          </Container>
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="instacart-container d-md-none">
                      <InstacartButton
                        meal={meal}
                        publicName={meal.publicName}
                        preparationImageUrl={meal.preparationImageUrl}
                      />
                      {FRISCO && <FriscoButton meal={meal} publicName={meal.publicName} />}
                    </div>

                    <div className="meal-info">
                      <div className="d-none d-md-block">
                        {renderTags()}
                        <h1 className="meal-title title-large semi-bold mb-xl">
                          {meal.publicName}
                        </h1>
                      </div>

                      <DayPlanMealRating
                        rateMeal={rateMeal}
                        refetch={refetch}
                        meal={meal}
                        reference={ratingRef}
                      />

                      {calculatedPreparationTime && (
                        <div className="grey-box mb-2">
                          {t('mealDetails.info.prepTime.title')}{' '}
                          <strong className="float-end">{calculatedPreparationTime}</strong>
                        </div>
                      )}

                      <div className="d-none d-md-block">
                        <DayPlanNutritions kcal={meal.kcal} macro={meal.macro} />
                      </div>

                      <div className="action-buttons d-none d-md-block">
                        <Row className="gx-0">
                          <Col xs={6}>
                            <Button
                              color="link"
                              size="sm"
                              className="w-100"
                              onClick={() => toggleInsertDate(meal.id)}
                            >
                              <img src={addToMealplan} alt="" className="me-2" />
                              {t('meal.favorites.mealDetails.addMealToMenu.button')}
                            </Button>
                          </Col>
                          <Col xs={6}>
                            <Button
                              color="primary"
                              size="sm"
                              className="w-100 px-3 tracker"
                              onClick={handleAddMealToTracker}
                            >
                              <img
                                src={addToTrackerIcon}
                                alt={t('tracker.meal.addToTrackerButton')}
                                className="me-2"
                              />
                              {t('tracker.meal.addToTrackerButton')}
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      <div className="d-none d-md-block mt-xl">
                        <InstacartButton
                          meal={meal}
                          publicName={meal.publicName}
                          preparationImageUrl={meal.preparationImageUrl}
                        />
                        <FriscoButton meal={meal} publicName={meal.publicName} />
                      </div>
                    </div>
                  </Col>
                </Row>

                {children()}

                <div className="d-md-none">
                  <DayPlanNutritions kcal={meal.kcal} macro={meal.macro} />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <footer className="d-md-none">
        <Container className="px-3">
          <Row className="gx-0">
            <Col xs={5}>
              <Button
                color="link"
                size="sm"
                className="w-100 ps-0"
                onClick={() => toggleInsertDate(meal.id)}
              >
                <img src={addToMealplan} alt="" className="me-2" />
                {t('meal.favorites.mealDetails.addMealToMenu.button')}
              </Button>
            </Col>
            <Col xs={7}>
              <Button
                color="primary"
                size="sm"
                className="w-100 px-3 tracker"
                onClick={handleAddMealToTracker}
              >
                <img
                  src={addToTrackerIcon}
                  alt={t('tracker.meal.addToTrackerButton')}
                  className="me-2"
                />
                {t('tracker.meal.addToTrackerButton')}
              </Button>
            </Col>
          </Row>
        </Container>
      </footer>

      <Modal isOpen={showModal} toggle={toggleModal} centered size="md">
        <ModalBody>
          <h4 className="title-medium semi-bold">{t('tracker.logMealError.popup.title')}</h4>
          <p className="text-text-variant-color multi-line mb-0">
            {t('tracker.logMealError.popup.description')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            tag={Link}
            to="/tracker/settings"
            className="w-100"
            onClick={toggleModal}
          >
            {t('tracker.logMealError.popup.settingsButton')}
          </Button>
          <Button color="link" className="w-100" onClick={toggleModal}>
            {t('tracker.logMealError.popup.cancelButton')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default FavMealDetails;
